import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import useLocalStorage from "./LocalStorage";
// eslint-disable-next-line no-unused-vars
import { soloCreateHome, soloGetHomes, soloGetScenarioAuthToken, soloLeaveHome, soloLoginResendSMS, soloLoginSendSMS, soloLoginVerifyToken, soloRegisterPush, soloUnregisterPush } from "./SoloServices";
import { isInDemoMode } from "app/home/general-components/BrowserUtils";
import packageJson from '../../../package.json';

function setActiveTime(homeSubSet, searchKey, searchValue) {
    const result = homeSubSet.map((h) => {
        if (h[searchKey] === searchValue) {
            const h2 = { ...h };
            h2.lastActiveTime = (new Date()).getTime();
            return h2
        }
        return h;
    });
    return result;
}

export default function useHomesManager(swController) {
    const [hubs, setHubs] = useLocalStorage('hubs', [], 'homes');
    const [soloHomes, setSoloHomes] = useLocalStorage('soloHomes', undefined);
    const [authTokens, setAuthTokens] = useLocalStorage('authTokens', {});
    const [logFilters, setLogFilters] = useLocalStorage('logFilters', {});
    const [soloAuthToken, setSoloAuthToken] = useLocalStorage('soloAuthToken', undefined);
    // eslint-disable-next-line no-unused-vars
    const [soloScenarioAuthToken, setSoloScenarioAuthToken] = useLocalStorage('soloScenarioAuthToken', undefined);
    const [soloPushSubscriptionStr, setSoloPushSubscriptionStr] = useLocalStorage('soloPushSubscriptionStr', undefined); // it is the last data sent to server

    const currentPushSubscriptionStr = swController.pushSubscriptionStr;
    useEffect(() => {
        if ((soloAuthToken !== undefined) && (currentPushSubscriptionStr !== undefined)) { // got the subscription
            if (soloPushSubscriptionStr === undefined) {
                // did not send to server before
                soloRegisterPush(soloAuthToken, currentPushSubscriptionStr, () => {
                    setSoloPushSubscriptionStr(currentPushSubscriptionStr)
                }, undefined)
            } else if ((soloPushSubscriptionStr !== undefined) && (soloPushSubscriptionStr !== currentPushSubscriptionStr)) {
                // probably expired
                soloUnregisterPush(soloAuthToken, soloPushSubscriptionStr, () => {
                    soloRegisterPush(soloAuthToken, currentPushSubscriptionStr, () => {
                        setSoloPushSubscriptionStr(currentPushSubscriptionStr)
                    }, undefined)
                }, undefined)
            }
        }
    }, [currentPushSubscriptionStr, soloPushSubscriptionStr, soloAuthToken, setSoloPushSubscriptionStr]);

    useEffect(() => {
        if ((soloAuthToken !== undefined) && (soloScenarioAuthToken === undefined)) {
            soloGetScenarioAuthToken(soloAuthToken, (t) => {
                setSoloScenarioAuthToken(t)
            })
        }
    }, [soloAuthToken, soloScenarioAuthToken, setSoloScenarioAuthToken]);

    const updateSoloHomes = useCallback((list) => {
        const newList = [];
        for (let i = 0; i < list.length; i++) {
            const newH = list[i];
            if (soloHomes !== undefined) {
                for (let j = 0; j < soloHomes.length; j++) {
                    const lastH = soloHomes[j];
                    if (lastH.home_id === newH.home_id) {
                        newH.lastActiveTime = lastH.lastActiveTime;
                        break;
                    }
                }
            }
            newList.push(newH);
        }
        setSoloHomes(newList);
    }, [setSoloHomes, soloHomes])

    const isSoloLoggedIn = soloAuthToken !== undefined;
    const [soloLoginTempId, setSoloLoginTempId] = useState(undefined);
    const hasLoginTempId = soloLoginTempId !== undefined;
    const soloSendSMS = useCallback((phoneNumber, language) => {
        soloLoginSendSMS(phoneNumber, language, (tempId) => {
            setSoloLoginTempId(tempId);
        });
    }, []);
    const soloResendSMS = useCallback(() => {
        soloLoginResendSMS(soloLoginTempId);
    }, [soloLoginTempId]);
    const soloVerifyToken = useCallback((smsToken, okCallback, errorCallback) => {
        soloLoginVerifyToken(soloLoginTempId, smsToken, (authToken) => {
            setSoloAuthToken(authToken);
            okCallback(authToken);
        }, errorCallback);
    }, [soloLoginTempId, setSoloAuthToken]);

    const isSoloHomesLoaded = soloHomes !== undefined;
    const reloadSoloHomes = useCallback((okCallback) => {
        if (soloAuthToken !== undefined)
            soloGetHomes(soloAuthToken, (list) => {
                updateSoloHomes(list);
                if (okCallback) {
                    okCallback()
                }
            });
    }, [soloAuthToken, updateSoloHomes]);
    // const reloadSoloHomesWithAuthToken = useCallback((at) => {
    //     soloGetHomes(at, (list) => {
    //         updateSoloHomes(list);
    //     });
    // }, [updateSoloHomes]);

    const homes = useMemo(() => {
        const all = [];
        // add hub to all
        for (let i = 0; i < hubs.length; i++) {
            const hub = { ...hubs[i] };
            hub.readyForConnection = true;
            hub.remove = () => {
                setHubs(hubs.filter((_hub) => {
                    return _hub.phoneNumber !== hub.phoneNumber;
                }));
                const _authTokens2 = { ...authTokens };
                delete _authTokens2[hub.phoneNumber];
                setAuthTokens(_authTokens2);

                const _logFilters2 = { ...logFilters };
                delete _logFilters2[getHomeIdForLogFilter(hub)];
                setLogFilters(_logFilters2);
            };
            hub.switch = () => {
                setHubs(setActiveTime(hubs, "phoneNumber", hub.phoneNumber));
            };
            all.push(hub);
        }
        // add solo to all
        if (soloHomes !== undefined) {
            for (let i = 0; i < soloHomes.length; i++) {
                const solo = { ...soloHomes[i] };
                solo.readyForConnection = true;
                solo.isSolo = true;
                solo.remove = (leaveAndDelete, okCallback) => {
                    soloLeaveHome(soloAuthToken, solo.home_id, leaveAndDelete, () => { // ok
                        const _logFilters2 = { ...logFilters };
                        delete _logFilters2[getHomeIdForLogFilter(solo)];
                        setLogFilters(_logFilters2);

                        reloadSoloHomes(okCallback);
                    }, () => {
                        // error
                    });
                };
                solo.switch = () => {
                    setSoloHomes(setActiveTime(soloHomes, "home_id", solo.home_id));
                };
                all.push(solo);
            }
        }
        // logFilters
        for (let i = 0; i < all.length; i++) {
            const aHome = all[i];
            const homeIdForLogFilter = getHomeIdForLogFilter(aHome);
            if (logFilters[homeIdForLogFilter]) {
                aHome.logFilter = logFilters[homeIdForLogFilter];
            } else {
                aHome.logFilter = emptyLogFilter;
            }
            aHome.setLogFilter = (newLogFilter) => {
                let _newLogFilters = {...logFilters};
                _newLogFilters[homeIdForLogFilter] = newLogFilter;
                setLogFilters(_newLogFilters);
            }
        }
        return all;
    }, [hubs, soloHomes, setHubs, setSoloHomes, soloAuthToken, reloadSoloHomes,
        authTokens, setAuthTokens, logFilters, setLogFilters]);
    // a hub is: { isSolo: undefined, phoneNumber, name, avatar, authToken, lastActiveTime, dontShowUpdatePopupBefore, remove, switch }
    // a soloHome is: { isSolo: true, home_id, name, avatar, lastActiveTime, remove, switch }

    const logoutFromSolo = useCallback((okCallback) => {
        if (soloPushSubscriptionStr !== undefined) {
            soloUnregisterPush(soloAuthToken, soloPushSubscriptionStr, () => {
                setSoloAuthToken(undefined);
                setSoloScenarioAuthToken(undefined);
                setSoloLoginTempId(undefined);
                setSoloHomes(undefined);
                setSoloPushSubscriptionStr(undefined)
                if (okCallback)
                    okCallback();
            }, undefined)
        } else {
            setSoloAuthToken(undefined);
            setSoloScenarioAuthToken(undefined);
            setSoloLoginTempId(undefined);
            setSoloHomes(undefined);
            if (okCallback)
                okCallback();
        }
    }, [soloAuthToken, setSoloAuthToken, setSoloScenarioAuthToken, setSoloHomes, soloPushSubscriptionStr, setSoloPushSubscriptionStr]);

    const genAuthToken = useCallback((phoneNumber) => {
        // if (authTokens.hasOwnProperty(phoneNumber)) {
        if (Object.prototype.hasOwnProperty.call(authTokens, phoneNumber)) {
            return authTokens[phoneNumber];
        } else {
            const at = uuidv4();
            // const at = "at_sarah";
            authTokens[phoneNumber] = at;
            setAuthTokens(authTokens);
            return at;
        }
    }, [authTokens, setAuthTokens]);

    const findHub = useCallback((phoneNumber) => {
        for (let i = 0; i < homes.length; i++) {
            const home = homes[i];
            if (home.isSolo !== true) {
                if (home.phoneNumber === phoneNumber) {
                    return home;
                }
            }
        }
        return undefined;
    }, [homes]);

    // this also do the .switch()
    const addHub = useCallback((phoneNumber, authToken) => {
        if (findHub(phoneNumber) === undefined) {
            const newHubs1 = [...hubs, { phoneNumber, authToken, lastActiveTime: (new Date()).getTime() }];
            setHubs(newHubs1)
            setTimeout(() => {
                window.location.reload(); // reload after switch to clear memory (and ws connection)
            }, 1000);
        }
    }, [setHubs, findHub, hubs]);

    const addSolo = useCallback((newHomeContent, homeIdCallback, errorCallback) => {
        soloCreateHome(soloAuthToken, newHomeContent, (homeId) => { // ok
            reloadSoloHomes();
            homeIdCallback(homeId);
        }, errorCallback);
    }, [soloAuthToken, reloadSoloHomes]);

    const activeHome = useMemo(() => {
        let lastActiveHome = undefined;
        let lastActiveHomeTime = -1;
        for (let i = 0; i < homes.length; i++) { // get latest active home
            const home = homes[i];
            if (home.lastActiveTime > lastActiveHomeTime) {
                lastActiveHome = home;
                lastActiveHomeTime = home.lastActiveTime;
            }
        }

        if (lastActiveHome) { // return it if exists
            return lastActiveHome;
        }
        return undefined;
    }, [homes]);    

    const [inOrdinaryAddHomeProcess, _setInOrdinaryAddHomeProcess] = useState(false);
    const inNewHomeProcess = inOrdinaryAddHomeProcess || (activeHome === undefined);
    const startAddHomeProcess = useCallback(() => {
        _setInOrdinaryAddHomeProcess(true);
    }, []);
    const dismissAddHomeProcess = useCallback(() => {
        _setInOrdinaryAddHomeProcess(false);
    }, []);

    const switchToHubIfExists = useCallback((phoneNumber) => {
        const theHub = findHub(phoneNumber);
        if (theHub !== undefined) {
            theHub.switch();
            dismissAddHomeProcess();
            setTimeout(() => {
                window.location.reload(); // reload after switch to clear memory (and ws connection)
            }, 1000);
            return true;
        }
        return false;
    }, [findHub, dismissAddHomeProcess]);

    const onGWProfile = useCallback((message) => {
        const avatar = message.msg.avatar;
        const name = message.msg.name;
        const phoneNumber = message.msg.phone_number;
        // const currentVersionName = message.msg.current_version_name;
        // const currentVersionId = message.msg.current_version_id;
        // const isNewerVersionAvailable = message.msg.is_newer_version_available;
        // const updateSeverityLevel = message.msg.update_severity_level;

        let newHubs = hubs.map((hub) => {
            if (phoneNumber !== hub.phoneNumber) {
                return hub;
            } else {
                const hub2 = { ...hub }
                hub2.avatar = avatar;
                hub2.name = name;
                return hub2;
            }
        });

        setHubs(newHubs);
    }, [setHubs, hubs]);

    const onPushToken = useCallback((message) => {
        const phoneNumber = message.msg.phone_number;
        const token = message.msg.token;

        let newHubs = hubs.map((hub) => {
            if (phoneNumber !== hub.phoneNumber) {
                return hub;
            } else {
                const hub2 = { ...hub }
                hub2.pushSubscriptionStr = token;
                return hub2;
            }
        });

        setHubs(newHubs);
    }, [setHubs, hubs]);

    const onSoloHomeProfile = useCallback((message) => {
        const avatar = message.msg.avatar;
        const name = message.msg.name;

        let newSoloHomes = soloHomes.map((sh) => {
            if (activeHome.home_id !== sh.home_id) {
                return sh;
            } else {
                const sh2 = { ...sh }
                if ((avatar !== undefined) && (avatar !== null) && (avatar > 0))
                    sh2.avatar = avatar;
                if ((name !== undefined) && (name !== null) && (name.length > 0))
                    sh2.name = name;
                return sh2;
            }
        });

        setSoloHomes(newSoloHomes);
    }, [setSoloHomes, soloHomes, activeHome]);

    const snoozUpdatePopup = useCallback((phoneNumber) => {
        let newHomes = hubs.map((hub) => {
            if (phoneNumber !== hub.phoneNumber) {
                return hub;
            } else {
                const hub2 = { ...hub }

                let today = new Date();
                let tomorrow = new Date();
                tomorrow.setDate(today.getDate() + 1); // 1d
                tomorrow.setHours(3); // at 3am

                hub2.dontShowUpdatePopupBefore = tomorrow.getTime();
                // hub2.dontShowUpdatePopupBefore = today.getTime() + 1000 * 10;  // 10s

                return hub2;
            }
        });

        setHubs(newHomes);
    }, [setHubs, hubs]);

    const isStaging = (packageJson.serverName === "staging");
    const demoMode = isInDemoMode();
    useEffect(() => {
        let demoHubPhoneNumber = isStaging ? "+000657166232" : "+000539824712";  // TODO: add production-co
        if (demoMode) {
            if (findHub(demoHubPhoneNumber) === undefined) {
                addHub(demoHubPhoneNumber, genAuthToken(demoHubPhoneNumber))
            }
        }
    }, [isStaging, demoMode, findHub, addHub, genAuthToken]);

    return {
        inNewHomeProcess,
        inOrdinaryAddHomeProcess, startAddHomeProcess, dismissAddHomeProcess,

        soloAuthToken, soloScenarioAuthToken,
        genAuthToken,
        activeHome, homes, addHub, addSolo,
        switchToHubIfExists,
        snoozUpdatePopup,
        currentPushSubscriptionStr,
        onGWProfile, onSoloHomeProfile, onPushToken,
        isSoloLoggedIn, soloSendSMS, hasLoginTempId, soloResendSMS, soloVerifyToken,
        isSoloHomesLoaded, reloadSoloHomes, soloHomes,
        logoutFromSolo,
    };
}

const emptyLogFilter = {};
function getHomeIdForLogFilter(home) {
    if (home.isSolo === true) {
        return "solo-" + String(home.home_id)
    } else {
        return "hub-" + String(home.phoneNumber)
    }
}